import { useEffect, useState } from 'react';
import api from '../api';
import { MessageCircle, CreditCard, Calendar, CheckCircle2, Plug, X,
         Send, Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';

const ICON = { whatsapp: MessageCircle, pago: CreditCard, calendar: Calendar };
const ACCENT = { whatsapp: '#25D366', pago: 'var(--accent-solid)', calendar: '#4285F4' };

const card = { background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 12, padding: 20 };
const input = { width: '100%', padding: '9px 12px', background: 'var(--surface-3)', border: '1px solid var(--surface)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' };
const label = { fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 6 };
const btnPri = { background: 'linear-gradient(135deg,var(--accent-solid),var(--accent-2))', border: 'none', color: '#fff', padding: '9px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 };
const btnSec = { background: 'transparent', border: '1px solid var(--surface-2)', color: 'var(--text-3)', padding: '9px 18px', borderRadius: 8, fontSize: 12, cursor: 'pointer' };

export default function Integraciones() {
  const [items, setItems]   = useState(null);
  const [abierta, setAbierta] = useState(null);   // tipo en configuración
  const [datos, setDatos]   = useState({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError]   = useState(null);
  const [okMsg, setOkMsg]   = useState(null);

  const cargar = () => api.get('/config/integraciones').then(r => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { cargar(); }, []);

  const abrir = (item) => {
    setError(null); setOkMsg(null);
    setAbierta(item.tipo);
    const init = {};
    (item.campos || []).forEach(c => { init[c.key] = ''; });
    setDatos(init);
  };

  const conectar = async (item) => {
    setGuardando(true); setError(null); setOkMsg(null);
    try {
      await api.post(`/config/integraciones/${item.tipo}`, { proveedor: item.proveedor, datos });
      setOkMsg(`${item.nombre} conectado correctamente`);
      setAbierta(null);
      await cargar();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo conectar');
    } finally { setGuardando(false); }
  };

  const desconectar = async (item) => {
    if (!window.confirm(`¿Desconectar ${item.nombre}? Se borran las credenciales guardadas.`)) return;
    try { await api.delete(`/config/integraciones/${item.tipo}`); await cargar(); }
    catch (e) { setError(e?.response?.data?.detail || 'Error al desconectar'); }
  };

  if (items === null) return <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Cargando integraciones…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ ...card, background: 'var(--surface-3)', borderStyle: 'dashed', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <ShieldCheck size={18} color="var(--success)" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
          Conecta tus cuentas <b>una vez</b> y el agente podrá usarlas en las llamadas (mandar WhatsApp,
          generar links de pago) sin que montes nada técnico. Tus credenciales se guardan cifradas y nunca se muestran.
        </div>
      </div>

      {okMsg && <Banner tipo="ok" texto={okMsg} />}
      {error && !abierta && <Banner tipo="err" texto={error} />}

      {(items || []).map(item => {
        const Ico = ICON[item.tipo] || Plug;
        const conectada = item.conectada && item.conectada.estado === 'conectado';
        const cfg = (item.conectada && item.conectada.config) || {};
        const disponible = item.disponible !== false;
        return (
          <div key={item.tipo} style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: 'var(--surface-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Ico size={22} color={ACCENT[item.tipo] || 'var(--text-2)'} />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{item.nombre}</span>
                  {conectada && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: 'var(--success)' }}>
                      <CheckCircle2 size={13} /> Conectado
                    </span>
                  )}
                  {!disponible && <span style={{ fontSize: 10, color: 'var(--text-3)', border: '1px solid var(--surface-2)', borderRadius: 20, padding: '2px 8px' }}>Próximamente</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>{item.descripcion}</div>
                {conectada && cfg.display_phone_number && (
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6 }}>
                    📱 {cfg.display_phone_number} · {cfg.verified_name || ''}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {conectada
                  ? <button onClick={() => desconectar(item)} style={{ ...btnSec, borderColor: 'var(--danger)', color: 'var(--danger)' }}>Desconectar</button>
                  : <button onClick={() => abrir(item)} disabled={!disponible}
                      style={{ ...btnPri, opacity: disponible ? 1 : .4, cursor: disponible ? 'pointer' : 'not-allowed' }}>
                      <Plug size={14} /> Conectar
                    </button>}
              </div>
            </div>

            {/* Formulario de conexión */}
            {abierta === item.tipo && (
              <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--surface)', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {(item.campos || []).map(c => (
                  <div key={c.key}>
                    <label style={label}>{c.label}{c.requerido ? ' *' : ''}</label>
                    <input style={input} type={c.tipo === 'secreto' ? 'password' : 'text'}
                      value={datos[c.key] || ''} placeholder={c.ayuda || ''}
                      onChange={e => setDatos(d => ({ ...d, [c.key]: e.target.value }))} />
                    {c.ayuda && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{c.ayuda}</div>}
                  </div>
                ))}
                {error && <Banner tipo="err" texto={error} />}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => conectar(item)} disabled={guardando} style={btnPri}>
                    {guardando ? <><Loader2 size={14} className="spin" /> Verificando…</> : <><CheckCircle2 size={14} /> Conectar y verificar</>}
                  </button>
                  <button onClick={() => { setAbierta(null); setError(null); }} style={btnSec}>Cancelar</button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  Verificamos las credenciales con {item.proveedor === 'meta_cloud' ? 'Meta' : 'el proveedor'} antes de guardar.
                </div>
              </div>
            )}

            {/* WhatsApp conectado: prueba de envío */}
            {conectada && item.tipo === 'whatsapp' && <ProbarWhatsApp />}
          </div>
        );
      })}
    </div>
  );
}

function Banner({ tipo, texto }) {
  const ok = tipo === 'ok';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
      background: ok ? 'var(--success-soft)' : 'var(--danger-soft)',
      color: ok ? 'var(--success)' : 'var(--danger)',
      border: `1px solid ${ok ? 'var(--success)' : 'var(--danger)'}`, borderRadius: 8, padding: '9px 12px' }}>
      {ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}{texto}
    </div>
  );
}

function ProbarWhatsApp() {
  const [plantillas, setPlantillas] = useState(null);
  const [tel, setTel] = useState('');
  const [sel, setSel] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => {
    api.get('/config/integraciones/whatsapp/plantillas')
      .then(r => setPlantillas(r.data || []))
      .catch(() => setPlantillas([]));
  }, []);

  const enviar = async () => {
    setEnviando(true); setRes(null);
    try {
      const body = { telefono: tel };
      if (sel) { body.plantilla = sel; body.idioma = (plantillas.find(p => p.name === sel) || {}).language || 'es'; }
      else { body.texto = 'Hola, esta es una prueba de tu agente de voz InnovaCall.'; }
      await api.post('/config/integraciones/whatsapp/probar', body);
      setRes({ ok: true, msg: 'Mensaje enviado ✓' });
    } catch (e) {
      setRes({ ok: false, msg: e?.response?.data?.detail || 'No se pudo enviar' });
    } finally { setEnviando(false); }
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--surface)' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 10 }}>Enviar un mensaje de prueba</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 150 }}>
          <label style={label}>Número (con o sin 57)</label>
          <input style={input} value={tel} onChange={e => setTel(e.target.value)} placeholder="3001234567" />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <label style={label}>Plantilla {plantillas && plantillas.length === 0 ? '(ninguna aprobada aún)' : ''}</label>
          <select style={input} value={sel} onChange={e => setSel(e.target.value)}>
            <option value="">Texto libre (solo ventana 24h)</option>
            {(plantillas || []).map(p => <option key={p.name} value={p.name}>{p.name} · {p.language}</option>)}
          </select>
        </div>
        <button onClick={enviar} disabled={enviando || !tel} style={btnPri}>
          {enviando ? <><Loader2 size={14} className="spin" /> Enviando…</> : <><Send size={14} /> Probar</>}
        </button>
      </div>
      {res && <div style={{ marginTop: 10 }}><Banner tipo={res.ok ? 'ok' : 'err'} texto={res.msg} /></div>}
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 8 }}>
        Nota: en frío WhatsApp solo permite iniciar con una plantilla aprobada por Meta. El texto libre funciona
        cuando la persona ya te escribió (ventana de 24 horas).
      </div>
    </div>
  );
}
